import { useEffect, useRef, useState } from "react"
import { Dialog, DialogContent, DialogTrigger } from "@/shared/ui/dialog.tsx"
import { Button } from "@/shared/ui/button.tsx"
import ImageGrid from "./ImageGrid"
import { SocialPostType } from "../edit-post/types"
import { useGetCatalog } from "../edit-post/api/single-post-api"
import { Heart, Search, Upload, X } from "lucide-react"
import { useGetGallery, useGetWishlist, useSaveImage, useUpdatePostBody } from "./api"
import { motion, AnimatePresence } from "motion/react"
import UploadImageInput from "./UploadImageInput"
import { Input } from "../ui/input.tsx"
import { useAtomValue } from "jotai"
import { themeAtom } from "@/features/app-layout/atoms/theme-atoms"
import { Card } from "../ui/card.tsx"
import ContextProvider from "./context/ContextProvider"
import { getSelectedImage } from "@/lib/funcs"

type Props = {
  content: SocialPostType["content"][number],
  trigger: JSX.Element,
  onSave?: (url: string) => void
}

const GalleryDialog = ({ content, trigger, onSave }: Props) => {

  const hasInitializedRef = useRef(false);

  const [open, setOpen] = useState(false)

  const theme = useAtomValue(themeAtom)

  const [showUpload, setShowUpload] = useState(false)

  const [showFav, setShowFav] = useState(false)

  const [catalogItem, setCatalogItem] = useState(content.catalogue)

  const { gallery, fetchNextPage, hasNextPage } = useGetGallery(open)

  const [selectedImage, setSelectedImage] = useState<number | undefined>(undefined);

  const { wishlist, fetchNextPage: fetchNextWishlist, hasNextPage:hasNextWishlist } = useGetWishlist(open)

  const { catalogData } = useGetCatalog(open)

  useEffect(() => {
    if (gallery && !hasInitializedRef.current) {
      const imageId = getSelectedImage(gallery, catalogItem, content.post_id);
      if (imageId !== undefined) {
        setSelectedImage(imageId);
      }
      hasInitializedRef.current = true;
    }
  }, [content, gallery])

  const { saveImage } = useSaveImage()

  const { updatePostBody } = useUpdatePostBody()

  const handleImageSelect = (image: number) => {
    console.log(image)
    setSelectedImage(image)
  }

  const [value, setValue] = useState("")

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setValue(newValue)
  }

  const handleReset = () => {
    setValue("")
  }

  const handleSubmit = () => {
    console.log(`Image ${selectedImage} selected`)
    if (selectedImage) {
      saveImage({ post_id: content.post_id, image_id: selectedImage })
      setOpen(false)
      const currentImageBody = gallery!
        .flatMap(catalog => catalog.images)
        .find(image => image.id === selectedImage);
      if (currentImageBody && onSave) {
        onSave(currentImageBody.image_url)
      }
    }

  }

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 300 : -300,
      opacity: 0
    })
  };

  const direction = showUpload ? 1 : -1;

  return (
    <ContextProvider>
      <Dialog
        open={open}
        onOpenChange={(newOpen) => {
          setOpen(newOpen)
          if (!newOpen) {
            setSelectedImage(undefined)
          }
        }}
      >
        <DialogTrigger asChild>
          {trigger}
        </DialogTrigger>
        <DialogContent className="max-w-6xl p-0 h-[90vh] overflow-hidden">
          <div className="flex flex-col h-[90vh]">
            <div className="flex flex-col items-start gap-2 p-4 border-b bg-background">
              <h2 className="text-xl font-montserrat font-medium">Select Image</h2>
              {!showUpload &&
                <div className="flex flex-col gap-2 w-full">
                  <div className="w-full flex items-center pb-2 gap-2">
                    <div className="flex-1 h-full relative">
                      <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500 dark:text-gray-400" />
                      <Input
                        type="text"
                        className="pl-9 pr-9 h-10"
                        placeholder="Search for Image"
                        value={value}
                        onChange={handleChange}
                      />

                      {value && (
                        <button
                          type="button"
                          onClick={handleReset}
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 focus:outline-none"
                          aria-label="Clear search"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        style={{ backgroundColor: theme.primary }}
                        onClick={() => {
                          setShowFav(false)
                          setShowUpload(prev => !prev)
                        }}
                        className="flex items-center gap-1">
                        <Upload />
                        Add Image
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between w-full border-y">
                    <div className="w-full flex flex-wrap items-center gap-2 py-3">
                      {catalogData && catalogData.catalogue.map(item => (
                        <Card
                          key={item.name}
                          onClick={() => {
                            setShowFav(false)
                            setShowUpload(false)
                            setCatalogItem(item.unique_identifier)
                            updatePostBody({
                              catalogue: item.unique_identifier,
                              post_content: content.post_content,
                              post_time: content.post_time,
                              post_date: content.post_date,
                              hashtags: content.tags,
                              post_id: content.post_id
                            })
                          }}
                          style={{
                            backgroundColor: catalogItem === item.unique_identifier ? theme.primary : "white",
                            color: catalogItem === item.unique_identifier ? "white" : "black"
                          }} className="px-3 py-2 text-sm font-poppins cursor-pointer">{item.name}</Card>
                      ))}
                    </div>
                    <Button onClick={() => {
                      setShowUpload(false)
                      setShowFav(true)
                    }} style={{ backgroundColor: theme.secondary }} className='flex items-center gap-2'>
                      <Heart />
                      Favourites
                    </Button>
                  </div>
                </div>
              }
            </div>

            <AnimatePresence initial={false} custom={direction} mode="wait">
              {(!showUpload && !showFav) && <motion.div key="first"
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  duration: 0.5,
                  ease: "easeInOut"
                }}
                className="flex-1 overflow-y-auto p-4">
                {gallery &&
                  <ImageGrid
                    galleryImages={gallery.find(item => item.catalogue === catalogItem)?.images}
                    selectedImage={selectedImage}
                    onSelect={handleImageSelect}
                    fetchNextPage={fetchNextPage}
                    searchValue={value}
                    hasNextPage={hasNextPage}
                  />
                }
              </motion.div>}

              {(showUpload && !showFav) && <motion.div key="second"
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  duration: 0.5,
                  ease: "easeInOut"
                }} className="flex-1 overflow-y-auto p-4">
                {catalogData &&
                  <UploadImageInput
                    setCatalog={setCatalogItem}
                    setShowUpload={setShowUpload}
                    catalogData={catalogData.catalogue}
                    onSelect={handleImageSelect} prompt={content.image_video_recommendation} />
                }
              </motion.div>
              }

              {(showFav && !showUpload) && (
                <motion.div key="third"
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{
                    duration: 0.5,
                    ease: "easeInOut"
                  }}
                  className="flex-1 overflow-y-auto p-4">
                  {wishlist &&
                    <ImageGrid
                      hasNextPage={hasNextWishlist}
                      galleryImages={wishlist}
                      fetchNextPage={fetchNextWishlist}
                      selectedImage={selectedImage}
                      onSelect={handleImageSelect}
                      searchValue={value}
                    />
                  }
                </motion.div>
              )}

            </AnimatePresence>

            <div className="flex justify-end p-4 border-t bg-background">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button
                  style={{
                    backgroundColor: theme.primary
                  }}
                  onClick={() => setShowUpload(prev => !prev)}>
                  {showUpload ? "Choose from gallery" : "Upload"}
                </Button>
                {!showUpload &&
                  <Button
                    style={{
                      backgroundImage: `linear-gradient(45deg, ${theme.primary}, ${theme.secondary})`,
                      backgroundSize: "200%"
                    }}
                    className="animate-wave"
                    disabled={selectedImage === undefined} onClick={handleSubmit}>
                    Submit
                  </Button>
                }
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </ContextProvider>
  )
}

export default GalleryDialog
